import React, { useEffect, useMemo, useState } from 'react';
import Select, { ValueType } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import Modal from '../../ui/Model';
import Button, { ButtonType } from '../../shared/components/core-ui/button/button.component';
import { LeadConfigModel } from '../../shared/models/lead.model';
import { detailFieldFor, DetailFieldConfig, LookupSource } from './lead-detail-fields.config';
import {
  AdType,
  MarketingChannel,
  MessagingChannel
} from '../../shared/interfaces/attribution.interface';
import { LeadUiModel } from '../../shared/ui-models/lead.ui-model';
import { changedFields, detailInputsFrom, formStateFrom, FormState } from './lead-form.util';
import { AreaUiModel } from '../../shared/ui-models/area.ui-model';
import { UnitTypeGroupUiModel } from '../../shared/ui-models/unit-type-group.ui-model';
import { ErrorInfo, isErrorInfo } from '../../shared/interfaces/error-info.interface';
import {
  getAllAreas,
  getAllTypeOfUnit,
  getAllUnitTypeGroups
} from '../../shared/services/data/lookups.data';

const selectMenuPortalStyles = {
  menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 })
};

const LABEL = 'mb-1 block text-sm font-gilroy-medium text-neutral-400';
const INPUT = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm';

interface Option {
  value: string;
  label: string;
}

const titleCase = (value: string): string =>
  (value || '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/^./, (c) => c.toUpperCase());

const toOptions = (values: string[]): Option[] =>
  values.map((value: string) => ({ value, label: titleCase(value) }));

interface LeadFormModalProps {
  show: boolean;
  config: LeadConfigModel;
  /** Absent means create. Present means edit that lead. */
  lead?: LeadUiModel;
  onClose: () => void;
  onSubmit: (payload: Record<string, unknown>) => void;
}

export const LeadFormModal: React.FC<LeadFormModalProps> = ({
  show,
  config,
  lead,
  onClose,
  onSubmit
}) => {
  const isEdit = Boolean(lead);
  const initial: FormState = useMemo(() => formStateFrom(lead), [lead]);

  const [vertical, setVertical] = useState(initial.vertical);
  const [source, setSource] = useState(initial.source);
  const [name, setName] = useState(initial.name);
  const [phone, setPhone] = useState(initial.phone);
  const [email, setEmail] = useState(initial.email);
  const [message, setMessage] = useState(initial.message);
  const [marketingChannel, setMarketingChannel] = useState(initial.marketingChannel);
  const [messagingChannel, setMessagingChannel] = useState(initial.messagingChannel);
  const [adType, setAdType] = useState(initial.adType);
  const [utmSource, setUtmSource] = useState(initial.utmSource);
  const [utmMedium, setUtmMedium] = useState(initial.utmMedium);
  const [utmCampaign, setUtmCampaign] = useState(initial.utmCampaign);
  const [marketingConsent, setMarketingConsent] = useState(initial.marketingConsent);
  const [consentSource, setConsentSource] = useState(initial.consentSource);
  const [details, setDetails] = useState<Record<string, string>>(detailInputsFrom(lead));
  const [lookups, setLookups] = useState<Record<LookupSource, string[]>>({
    areaNames: [],
    unitTypeGroups: [],
    typeOfUnits: []
  });

  /**
   * The lists this form offers instead of asking people to type from memory.
   *
   * Fetched once when the modal opens rather than per vertical: the same three
   * cover all of them, and a picker that populates after the operator has
   * already typed is worse than one that was there from the start.
   */
  useEffect(() => {
    if (!show) return;
    Promise.all([
      getAllAreas('LeadFormModal'),
      getAllUnitTypeGroups('LeadFormModal'),
      getAllTypeOfUnit('LeadFormModal')
    ]).then(([areas, groups, types]) => {
      setLookups({
        // `areaName` stores the name, not an id — the lead service records what
        // the person said the area was, which is not always one of ours.
        areaNames: isErrorInfo(areas as ErrorInfo)
          ? []
          : (areas as AreaUiModel[]).map((a: AreaUiModel) => a.name),
        unitTypeGroups: isErrorInfo(groups as ErrorInfo)
          ? []
          : (groups as UnitTypeGroupUiModel[]).map((g: UnitTypeGroupUiModel) => g.name),
        typeOfUnits: isErrorInfo(types as ErrorInfo)
          ? []
          : (types as { name: string }[]).map((t: { name: string }) => t.name)
      });
    });
  }, [show]);

  /**
   * Reopening resets to the lead as stored, discarding an abandoned edit.
   *
   * Keyed on `initial` as well as `show` because the details screen keeps one
   * modal mounted and swaps the lead into it — without that, opening the form
   * on a second lead would show the first one's values.
   */
  useEffect(() => {
    if (!show) return;
    setVertical(initial.vertical);
    setSource(initial.source);
    setName(initial.name);
    setPhone(initial.phone);
    setEmail(initial.email);
    setMessage(initial.message);
    setMarketingChannel(initial.marketingChannel);
    setMessagingChannel(initial.messagingChannel);
    setAdType(initial.adType);
    setUtmSource(initial.utmSource);
    setUtmMedium(initial.utmMedium);
    setUtmCampaign(initial.utmCampaign);
    setMarketingConsent(initial.marketingConsent);
    setConsentSource(initial.consentSource);
    setDetails(detailInputsFrom(lead));
  }, [show, initial]);

  const verticalOptions = useMemo(() => toOptions(config.verticals ?? []), [config]);
  const sourceOptions = useMemo(() => toOptions(config.sources ?? []), [config]);

  /**
   * Closed, unlike the detail lookups below.
   *
   * Attribution reporting groups by this, so a typed-in channel is a new row in
   * every dashboard rather than a new option. `unknown` is in the list for the
   * leads that genuinely have no attribution.
   */
  const channelOptions: Option[] = useMemo(
    () =>
      Object.values(MarketingChannel).map((value: string) => ({ value, label: titleCase(value) })),
    []
  );

  const messagingOptions: Option[] = useMemo(
    () =>
      Object.values(MessagingChannel).map((value: string) => ({ value, label: titleCase(value) })),
    []
  );

  const adTypeOptions: Option[] = useMemo(
    () => Object.values(AdType).map((value: string) => ({ value, label: titleCase(value) })),
    []
  );

  /**
   * Which detail keys this vertical accepts, from the service.
   *
   * Not a local list: the service rejects an unknown key outright rather than
   * dropping it, so a hardcoded copy that drifts turns into a 400 nobody can
   * act on. Changing the vertical clears details for the same reason — keys
   * from the old vertical would be rejected.
   */
  const detailKeys: string[] = vertical ? config.detailKeysByVertical?.[vertical] ?? [] : [];

  // The only hard rules the service enforces on create: a vertical, a source,
  // and at least one way to contact the person. Mirrored here so a known-bad
  // submit is a disabled button rather than a 400.
  const hasContact = phone.trim().length > 0 || email.trim().length > 0;
  // Consent without a source is rejected upstream: PDPL wants evidence of where
  // consent was captured, not just that it was.
  const consentComplete = !marketingConsent || consentSource.trim().length > 0;

  const setDetail = (key: string, value: string) =>
    setDetails((current) => ({ ...current, [key]: value }));

  const buildDetails = (): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    detailKeys.forEach((key: string) => {
      const raw = (details[key] ?? '').trim();
      if (!raw) return;
      const field: DetailFieldConfig = detailFieldFor(key);
      if (field.kind === 'number') {
        const n = Number(raw);
        if (!Number.isNaN(n)) out[key] = n;
        return;
      }
      if (field.kind === 'list') {
        const items = raw
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean);
        if (items.length) out[key] = items;
        return;
      }
      out[key] = raw;
    });
    return out;
  };

  /** The form as it stands, trimmed — the right-hand side of the edit diff. */
  const currentState = (): FormState => ({
    vertical,
    source,
    name: name.trim(),
    phone: phone.trim(),
    email: email.trim(),
    message: message.trim(),
    marketingChannel: marketingChannel.trim(),
    messagingChannel: messagingChannel.trim(),
    adType: adType.trim(),
    utmSource: utmSource.trim(),
    utmMedium: utmMedium.trim(),
    utmCampaign: utmCampaign.trim(),
    marketingConsent,
    consentSource: consentSource.trim(),
    // Built, not raw: numbers are numbers and lists are arrays by here, so the
    // diff compares what would actually be stored rather than what was typed.
    details: buildDetails()
  });

  const buildCreatePayload = (): Record<string, unknown> => {
    const payload: Record<string, unknown> = { vertical, source };
    if (name.trim()) payload.name = name.trim();
    if (phone.trim()) payload.phone = phone.trim();
    if (email.trim()) payload.email = email.trim();
    if (message.trim()) payload.message = message.trim();
    if (marketingChannel.trim()) payload.marketingChannel = marketingChannel.trim();
    if (messagingChannel.trim()) payload.messagingChannel = messagingChannel.trim();
    if (adType.trim()) payload.adType = adType.trim();
    if (utmSource.trim()) payload.utm_source = utmSource.trim();
    if (utmMedium.trim()) payload.utm_medium = utmMedium.trim();
    if (utmCampaign.trim()) payload.utm_campaign = utmCampaign.trim();
    if (marketingConsent) {
      payload.marketingConsent = true;
      payload.consentSource = consentSource.trim();
    }

    const built = buildDetails();
    if (Object.keys(built).length) payload.details = built;
    return payload;
  };

  const pendingChanges: Record<string, unknown> = isEdit
    ? changedFields(initial, currentState())
    : {};

  const canSubmit = isEdit
    ? hasContact && consentComplete && Object.keys(pendingChanges).length > 0
    : vertical.length > 0 && source.length > 0 && hasContact && consentComplete;

  const renderDetail = (key: string) => {
    const field: DetailFieldConfig = detailFieldFor(key);

    if (field.kind === 'lookup' || field.kind === 'list') {
      const isMulti = field.kind === 'list';
      const options: Option[] = (field.source ? lookups[field.source] : []).map(
        (value: string) => ({ value, label: value })
      );
      const raw = details[key] ?? '';
      const currentMulti: Option[] = raw
        ? raw.split(',').map((v) => ({ value: v.trim(), label: v.trim() }))
        : [];
      const currentSingle: Option | null = raw ? { value: raw, label: raw } : null;

      return (
        <div key={key}>
          <label className={LABEL}>{field.label}</label>
          {/*
            Creatable, not a plain Select. These lists describe our catalogue,
            and a lead records what the person actually said — an owner naming
            an area or a unit type we do not have yet is a real lead, not a
            typo. Offering the list without allowing anything else would force
            whoever takes the call to pick something untrue.
          */}
          <CreatableSelect
            isMulti={isMulti}
            isClearable
            menuPortalTarget={document.body}
            menuPlacement="auto"
            styles={selectMenuPortalStyles}
            classNamePrefix="react-select"
            options={options}
            value={isMulti ? currentMulti : currentSingle}
            placeholder={options.length ? 'Choose or type' : 'Type a value'}
            formatCreateLabel={(input: string) => `Use "${input}"`}
            noOptionsMessage={() => 'Type a value and press enter'}
            onChange={(value: ValueType<Option, boolean>) => {
              if (isMulti) {
                setDetail(key, ((value as Option[]) || []).map((o) => o.value).join(', '));
              } else {
                setDetail(key, value ? (value as Option).value : '');
              }
            }}
          />
          {field.hint ? <p className="mt-1 text-xs text-neutral-300">{field.hint}</p> : null}
        </div>
      );
    }

    return (
      <div key={key}>
        <label className={LABEL}>{field.label}</label>
        <input
          className={INPUT}
          type={field.kind === 'date' ? 'date' : field.kind === 'number' ? 'number' : 'text'}
          value={details[key] ?? ''}
          onChange={(event) => setDetail(key, event.target.value)}
        />
        {field.hint ? <p className="mt-1 text-xs text-neutral-300">{field.hint}</p> : null}
      </div>
    );
  };

  return (
    <Modal
      isOpen={show}
      onClose={onClose}
      containerClassName="lg:h-fit lg:max-h-[88vh] lg:w-[760px]"
      headerContent={
        <div className="text-center font-gilroy-bold text-2xl leading-[100%] text-neutral-500">
          {isEdit ? `Edit lead #${lead?.shortId}` : 'Create lead'}
        </div>
      }
      footerContent={
        <div className="flex items-center justify-end gap-4">
          {isEdit && Object.keys(pendingChanges).length > 0 ? (
            <span className="mr-auto text-xs text-neutral-300">
              {Object.keys(pendingChanges).length} field
              {Object.keys(pendingChanges).length === 1 ? '' : 's'} changed
            </span>
          ) : null}
          <Button type={ButtonType.gray} onClick={onClose}>
            Cancel
          </Button>
          <Button
            type={ButtonType.primary}
            disabled={!canSubmit}
            tracking={{
              event: 'record_saved',
              entity: 'lead',
              action: isEdit ? 'update' : 'create',
              outcome: 'success'
            }}
            onClick={() => onSubmit(isEdit ? pendingChanges : buildCreatePayload())}
          >
            {isEdit ? 'Save changes' : 'Create lead'}
          </Button>
        </div>
      }
    >
      <div className="flex w-full flex-col gap-5">
        <section>
          <h3 className="mb-2 font-gilroy-bold text-sm text-neutral-500">Required</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className={LABEL}>Vertical *</label>
              <Select
                menuPortalTarget={document.body}
                isSearchable={false}
                menuPlacement="auto"
                styles={selectMenuPortalStyles}
                classNamePrefix="react-select"
                options={verticalOptions}
                placeholder="Which funnel is this?"
                value={verticalOptions.find((o: Option) => o.value === vertical) ?? null}
                onChange={(option: ValueType<Option, false>) => {
                  setVertical(option ? (option as Option).value : '');
                  // Keys from the previous vertical would be rejected outright.
                  setDetails({});
                }}
              />
              {isEdit && vertical !== initial.vertical ? (
                <p className="mt-1 rounded-xl bg-creams-300 px-3 py-2 text-xs text-creams-900">
                  Moving this lead to another funnel clears its {titleCase(initial.vertical)}{' '}
                  details — the new vertical does not accept those fields.
                </p>
              ) : null}
            </div>

            <div>
              <label className={LABEL}>Source *</label>
              <Select
                menuPortalTarget={document.body}
                isSearchable={false}
                menuPlacement="auto"
                styles={selectMenuPortalStyles}
                classNamePrefix="react-select"
                options={sourceOptions}
                value={sourceOptions.find((o: Option) => o.value === source) ?? null}
                onChange={(option: ValueType<Option, false>) =>
                  setSource(option ? (option as Option).value : '')
                }
              />
            </div>

            <div>
              <label className={LABEL}>Phone</label>
              <input
                className={INPUT}
                value={phone}
                placeholder="01xxxxxxxxx"
                onChange={(event) => setPhone(event.target.value)}
              />
            </div>

            <div>
              <label className={LABEL}>Email</label>
              <input
                className={INPUT}
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>
          </div>
          {!hasContact ? (
            <p className="mt-2 rounded-xl bg-creams-300 px-3 py-2 text-xs text-creams-900">
              A lead needs at least a phone number or an email address — there is no way to work one
              without either.
            </p>
          ) : null}
        </section>

        <section>
          <h3 className="mb-2 font-gilroy-bold text-sm text-neutral-500">Optional</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className={LABEL}>Name</label>
              <input className={INPUT} value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <label className={LABEL}>Marketing channel</label>
              <Select
                isClearable
                menuPortalTarget={document.body}
                isSearchable={false}
                menuPlacement="auto"
                styles={selectMenuPortalStyles}
                classNamePrefix="react-select"
                options={channelOptions}
                placeholder="Unknown"
                value={channelOptions.find((o: Option) => o.value === marketingChannel) ?? null}
                onChange={(option: ValueType<Option, false>) =>
                  setMarketingChannel(option ? (option as Option).value : '')
                }
              />
            </div>
            <div>
              <label className={LABEL}>Campaign</label>
              <input
                className={INPUT}
                value={utmCampaign}
                onChange={(e) => setUtmCampaign(e.target.value)}
              />
            </div>
            <div>
              <label className={LABEL}>Messaging channel</label>
              {/*
                How the person talked to us, which is a different question from
                what brought them — a WhatsApp conversation started by a Google
                ad is both, and collapsing them loses one.
              */}
              <Select
                isClearable
                menuPortalTarget={document.body}
                isSearchable={false}
                menuPlacement="auto"
                styles={selectMenuPortalStyles}
                classNamePrefix="react-select"
                options={messagingOptions}
                placeholder="None"
                value={messagingOptions.find((o: Option) => o.value === messagingChannel) ?? null}
                onChange={(option: ValueType<Option, false>) =>
                  setMessagingChannel(option ? (option as Option).value : '')
                }
              />
            </div>
            <div>
              <label className={LABEL}>Ad type</label>
              <Select
                isClearable
                menuPortalTarget={document.body}
                isSearchable={false}
                menuPlacement="auto"
                styles={selectMenuPortalStyles}
                classNamePrefix="react-select"
                options={adTypeOptions}
                placeholder="Unknown"
                value={adTypeOptions.find((o: Option) => o.value === adType) ?? null}
                onChange={(option: ValueType<Option, false>) =>
                  setAdType(option ? (option as Option).value : '')
                }
              />
            </div>
            <div>
              <label className={LABEL}>UTM source</label>
              <input
                className={INPUT}
                value={utmSource}
                onChange={(e) => setUtmSource(e.target.value)}
              />
            </div>
            <div>
              <label className={LABEL}>UTM medium</label>
              <input
                className={INPUT}
                value={utmMedium}
                onChange={(e) => setUtmMedium(e.target.value)}
              />
            </div>
          </div>
          <div className="mt-3">
            <label className={LABEL}>Message</label>
            <textarea
              className={INPUT}
              rows={2}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>
        </section>

        <section>
          <h3 className="mb-2 font-gilroy-bold text-sm text-neutral-500">Marketing consent</h3>
          {/*
            Consent is a record, not a preference. Egypt's PDPL wants evidence
            of where it was captured and keeps that evidence for three years, so
            the source is required the moment the box is ticked. A lead without
            consent can still be worked by sales — it just may never be exported
            to a Meta or Google audience.
          */}
          <label className="flex items-center gap-2 text-sm text-neutral-500">
            <input
              type="checkbox"
              checked={marketingConsent}
              onChange={(event) => setMarketingConsent(event.target.checked)}
            />
            This person agreed to receive marketing
          </label>
          {marketingConsent ? (
            <div className="mt-2">
              <label className={LABEL}>Where consent was given *</label>
              <input
                className={INPUT}
                value={consentSource}
                placeholder="phone_call, web_form, whatsapp…"
                onChange={(event) => setConsentSource(event.target.value)}
              />
              {!consentComplete ? (
                <p className="mt-1 text-xs text-action-danger">
                  Required — consent with no recorded source is not evidence of consent.
                </p>
              ) : null}
            </div>
          ) : null}
        </section>

        <section>
          <h3 className="mb-1 font-gilroy-bold text-sm text-neutral-500">
            {vertical ? `${titleCase(vertical)} details` : 'Funnel details'}
          </h3>
          {!vertical ? (
            <p className="text-xs text-neutral-400">Pick a vertical to see its fields.</p>
          ) : detailKeys.length === 0 ? (
            <p className="text-xs text-neutral-400">This vertical has no extra fields.</p>
          ) : (
            <>
              <p className="mb-2 text-xs text-neutral-400">
                All optional. Each vertical accepts only its own fields — the service rejects the
                rest rather than dropping them.
              </p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {detailKeys.map(renderDetail)}
              </div>
            </>
          )}
        </section>
      </div>
    </Modal>
  );
};
